import { KeyboardEvent, useCallback } from 'react';

/**
 * Returns an `onKeyDown` handler for the field that should submit its form on Enter, exactly like
 * clicking the form's primary button. Attach it only to the field(s) that should submit.
 *
 * Pass the primary button's own disabled/loading condition as `disabled` so Enter can never bypass
 * it (e.g. a second Enter while the first submission is in flight).
 *
 * Ignored: a held Enter (key repeat), Enter while composing IME text, Enter already consumed by the
 * input (e.g. picking an Autocomplete option) and Enter on non-input targets inside the field, such
 * as the password show/hide button.
 */
const useSubmitOnEnter = (onSubmit: () => void, disabled = false) =>
  useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (
        e.key !== 'Enter' ||
        e.repeat ||
        e.nativeEvent.isComposing ||
        e.isDefaultPrevented() ||
        disabled ||
        !(e.target instanceof HTMLInputElement)
      ) {
        return;
      }
      onSubmit();
    },
    [onSubmit, disabled],
  );

export default useSubmitOnEnter;
